import {Feature, FeatureCollection, Point} from "geojson";
import {VisitorMovementFeatureProperties} from "../../../services/ngage/maps";
import distance from "@turf/distance";
import {DateTime} from "luxon";
import { featureCollection as createFeatureCollection } from "@turf/helpers";
import {type Option} from "../../SimpleSelect/SimpleSelect";
import {
  DISTANCE_THRESHOLD,
  DistanceThreshold,
  ZOOM_LEVELS,
  ZoomLevel
} from "../../../pages/Insights/View/VisitorMovement";
import {flyToBounds} from "../../../services/map";
import {RefObject} from "react";
import {MapRef} from "react-map-gl/mapbox";

/**
 * Get filtered visitor movement data
 */

// Types ----

export type FilteredVisitorMovementFeatureProperties = VisitorMovementFeatureProperties & {
  point: number
  occurred_at_tz: string
}

type FilteredVisitorMovementDataParams = {
  userUUID?: string | null
  featureCollection?: FeatureCollection<Point, VisitorMovementFeatureProperties>
  distanceThreshold?: DistanceThreshold
  zone?: string
  locale?: string
}

// Helper ----

export function getFilteredVisitorMovementData(featureCollection?: FeatureCollection<Point, VisitorMovementFeatureProperties>, distanceThreshold: DistanceThreshold = DISTANCE_THRESHOLD["10"]): FeatureCollection<Point, VisitorMovementFeatureProperties> {
  // Return an empty feature collection if we have no given feature collection
  if (!featureCollection) return createFeatureCollection<Point, FilteredVisitorMovementFeatureProperties>([]);

  // Group features by visitor
  const groupedFeatures = featureCollection.features.reduce((acc: Record<string, Feature<Point, VisitorMovementFeatureProperties>[]>, feature) => {
    const userId = feature.properties.user_uuid;
    if (!acc[userId]) acc[userId] = [];
    acc[userId].push(feature);

    return acc;
  }, {});

  // Filter out features which have exceeded the distance threshold
  const filteredFeatures = Object.entries(groupedFeatures).map(([_, features]) => {
    return features.filter((feature, index, array) => {
      // Always keep the first feature, no need to compare distance moved
      if (index === 0) return true;

      // Check if the distance threshold has been exceeded
      const previousFeature = array[index - 1];
      return exceededsDistanceThreshold(previousFeature, feature, distanceThreshold);
    });
  }).flat();

  return createFeatureCollection<Point, VisitorMovementFeatureProperties>(filteredFeatures);
}

export function getSingleVisitorMovementData({userUUID, featureCollection, zone = 'local', locale = 'en-GB'}: FilteredVisitorMovementDataParams): FeatureCollection<Point, FilteredVisitorMovementFeatureProperties> {
  // Return an empty feature collection if we have no given feature collection
  if (!userUUID || !featureCollection) return createFeatureCollection<Point, FilteredVisitorMovementFeatureProperties>([]);

  const features = featureCollection.features
    .filter(feature => feature.properties?.user_uuid === userUUID)
    .map((feature, index) => {
      return {
        ...feature,
        properties: {
          ...feature.properties,
          point: index + 1,
          occurred_at_tz: DateTime.fromMillis(feature.properties?.occurred_at ?? 0, {zone}).toLocaleString(DateTime.TIME_24_WITH_SECONDS, {locale}),
        }
      }
    });

  return createFeatureCollection<Point, FilteredVisitorMovementFeatureProperties>(features);
}

/**
 * Get visitor options
 */

export function getVisitorOptions(featureCollection: FeatureCollection<Point, VisitorMovementFeatureProperties>, distanceThreshold: number = 10): Option[] {
  // Calculate how many features each visitor has
  const visitorFeaturesCount = featureCollection.features
    .reduce((accumulator: Record<string, number>, feature: Feature) => {
    const visitorUUID = feature.properties?.user_uuid;
    if (visitorUUID) accumulator[visitorUUID] = (accumulator[visitorUUID] ?? 0) + 1;

    return accumulator;
  }, {});

  // Sort visitor UUIDs by highest feature count first
  const visitorUUIDs = Object.entries(visitorFeaturesCount)
    .sort(([, countA], [, countB]) => countB - countA)
    .map(([userUUID]) => userUUID);

  // Return the visitor UUIDs as options
  return visitorUUIDs.map((id, index) => {
    return {
      name: `Visitor ${index + 1}`,
      value: id,
    }
  })
}

/**
 * Fly to visitor point
 */

// Types ----

type FlyToVisitorPointParams = {
  point: number
  features: Feature<Point, FilteredVisitorMovementFeatureProperties>[]
  mapRef: RefObject<MapRef>
  zoomLevel?: ZoomLevel
}

// Helper ----

export function flyToVisitorPoint({point, features, mapRef, zoomLevel = ZOOM_LEVELS["2x"]}: FlyToVisitorPointParams) {
  if (!point || !features.length) return;

  const featureIndex = point - 1;

  if ('geometry' in features[featureIndex]) {
    flyToBounds(features[point - 1].geometry, mapRef, { zoom: zoomLevel });
  }
}

/**
 * Check if a feature has exceeded the distance threshold
 */

function exceededsDistanceThreshold(previousFeature: Feature<Point, VisitorMovementFeatureProperties>, currentFeature: Feature<Point, VisitorMovementFeatureProperties>, distanceThreshold: DistanceThreshold = DISTANCE_THRESHOLD["10"]) {
  // Compare distance moved between the previous feature and the current feature
  const distanceMovedInMetres = distance(previousFeature.geometry, currentFeature.geometry, { units: 'meters' });

  // Keep feature if distance moved exceeds the threshold
  return distanceMovedInMetres >= distanceThreshold;
}

